/**
 * The age bands: who is grown, who is not yet, who no longer is.
 *
 * The arithmetic half of the life cycle, deliberately **pure** and
 * import-light, on the `bindings.ts` model and for the same reason: the
 * readers are scattered across the tangle — `musterRoom` in `companies.ts`
 * needs the adult count and the refusal sentence, `killUnit` in `combat.ts`
 * needs to charge a death to the bands, `assignJobs` in `settlement.ts` needs
 * the worker pool — and a file that reads only `World` and `RaceDef` can be
 * imported by all of them without closing the `world → systems → world` cycle
 * the bindings note warns about. The *stepping* — maturation, eldering, the
 * grave — lives in `settlement.ts`, which already owns the population clock
 * and already imports `killUnit`.
 *
 * The design constraint, argued at length in `src/data/demography.ts`: age is
 * three floats on the world, never a per-model column. Everything here is
 * band arithmetic over `w.demography` and `w.counts.villagers`.
 *
 * ── Which heads are which ─────────────────────────────────────────────────
 *
 * The bands are aggregates, so no particular villager *is* a child — but the
 * job gate needs a deterministic answer to "which villagers work", and the
 * answer is ordinal: the first `adultVillagers(w)` live villagers in slot
 * order are the grown, and everyone after them is too young or too old to be
 * on the ladder. Slot reuse means the identity shifts as people are born and
 * die, which is invisible at population scale and exactly as deterministic as
 * everything else the tie-breaks in `assignJobs` rest on.
 */

import type { World } from '../world.ts';
import { race } from '../../data/races.ts';
import { MUSTER_GRACE_HEADS } from '../../data/demography.ts';

/**
 * A people the cycle passes over: their pop is raised, not born.
 *
 * Read off `RaceDef.ageless` — additive, and true today of exactly the Pale
 * Litany. The dead do not grow up, do not wear out, and are not divided into
 * generations; every head the Litany holds is a working head, its bands are
 * pinned to zero by `stepDemography`, and neither the job gate nor the muster
 * gate ever binds. An undead people whose skeletons died of old age would be
 * a joke at the design's expense.
 */
export function agelessPeople(w: World): boolean {
  return race(w.raceId).ageless === true;
}

/**
 * Villagers grown enough to hold a job or a spear.
 *
 * The effective worker pool: `assignJobs` employs at most this many hands and
 * `musterRoom` refuses a company the town has not got the grown bodies for.
 * **Rounded, not floored**, and the difference showed up in the harness
 * before it showed up in reason: the bands flow continuously, so a town of
 * twelve grown founders carries a fifth of an elder within the first month —
 * and a floor reads that as eleven adults, which deleted a real worker and a
 * real soldier for a head that has not aged yet. Round splits the fraction
 * instead of always charging it, and the all-children case still counts zero.
 * Clamped at zero because between a massacre and the next reconcile the
 * bands can momentarily exceed the living.
 */
export function adultVillagers(w: World): number {
  if (agelessPeople(w)) return w.counts.villagers;
  const d = w.demography;
  return Math.max(0, Math.round(w.counts.villagers - d.children - d.elders));
}

/**
 * How many of `liveVillagers` the job ladder will employ. `assignJobs` calls
 * this with the count it has just taken, which is exact where
 * `w.counts.villagers` is a tick stale on the pass that spawned somebody.
 *
 * Two peoples are exempt, each for its own written reason: the **ageless**
 * (every raised head is a working head), and the **moot** — the Splitmoot's
 * passive says a company is released "from whatever the warren happens to be
 * full of", and the one binding whose shape is "nothing refuses" should not be
 * the one people in the game whose hands are rationed by a gate no other part
 * of it observes. A goblin is born knowing how to carry.
 */
export function jobHands(w: World, liveVillagers: number): number {
  if (agelessPeople(w)) return liveVillagers;
  if (race(w.raceId).binding === 'moot') return liveVillagers;
  const d = w.demography;
  // Rounded for the reason `adultVillagers` is: a fraction of an elder must
  // not idle a whole mason.
  return Math.max(0, Math.round(liveVillagers - d.children - d.elders));
}

/** Adults across the whole roll, soldiers included. For readouts. */
export function adultsOf(w: World): number {
  if (agelessPeople(w)) return w.pop;
  const d = w.demography;
  return Math.max(0, Math.round(w.pop - d.children - d.elders));
}

/**
 * The founders. Twelve people who walked to the end of the world and lit a
 * fire there — **all of them grown**, which is not flavour but the opening
 * balance: the starting jobs want every pair of hands the town has, and the
 * first militia is fourteen shields against twelve adults plus the muster's
 * four heads of grace. Seeding a fifth of the founders as children (the old-
 * save default) would refuse that first company and under-staff the first
 * fields for a game-year, which is a punishment for starting a new run.
 *
 * The pyramid grows in from here rather than being handed over: the cradle
 * fills toward its sixth of the roll on `CHILDHOOD_MONTHS`' own time constant,
 * so a frontier camp is all hands and it is the established town that carries
 * dependants. That is the right way round — it is the settlement with fifteen
 * buildings and a wall that should be feeding people who cannot yet hold a
 * spear, not the twelve who have just put a fire down in the dark.
 */
export function seedDemography(w: World): void {
  w.demography.children = 0;
  w.demography.elders = 0;
  w.demography.grave = 0;
}

/**
 * Why this muster is refused for want of grown hands, or ''.
 *
 * The adult gate, and where it does and does not apply is the whole of how it
 * composes with the bindings rather than fighting them:
 *
 *   - **housing / irreplaceable / crews** — applies. These peoples raise
 *     companies out of the town, and a town of children has nobody to arm
 *     however many beds it has.
 *   - **mana** — never reached; the Litany is `ageless` and its pool is the
 *     binding. The dead are raised, not recruited.
 *   - **names** — exempt. The Vigil's passive says it is bounded by names and
 *     not by housing, and the bed test is deliberately not reached for it;
 *     bolting the adult gate on instead would be the same shape-and-stricter
 *     mistake that comment warns against.
 *   - **thralls** — exempt. The company is captured enemies, not townsfolk;
 *     the pens are the pool and the pens hold no children.
 *   - **moot** — exempt. The passive's own words: you release a company "from
 *     whatever the warren happens to be full of." The one binding with no
 *     capacity test anywhere keeps having none; goblins the press counts, the
 *     muster may spend.
 *
 * `MUSTER_GRACE_HEADS` of slack, mirroring the bed test's `popCap + 4` — see
 * the constant for why it is load-bearing at the opening.
 */
export function musterAdultRefusal(w: World, size: number): string {
  const grown = adultVillagers(w);
  if (size > grown + MUSTER_GRACE_HEADS) {
    return `Only ${grown} of them are grown enough to take a shilling. The rest are in the cradle or at the fire.`;
  }
  return '';
}

/**
 * True while `stepDemography` is taking an elder — its own kill settles the
 * ledger exactly (one head off `elders`), so the proportional charge below
 * must stand aside or the death would be booked twice.
 *
 * A module-level flag and not state: it is set and cleared inside a single
 * synchronous call in `stepDemography`, can never be observed across a tick,
 * a save or a hash, and exists only so `killUnit` does not need to grow a
 * parameter every caller but one would pass as `false`.
 */
let agedDeath = false;

export function markAgedDeath(v: boolean): void {
  agedDeath = v;
}

/**
 * A villager died. Called from the one door every real death goes through —
 * `killUnit`'s player-villager branch — so a sword, a tower shell, a poison
 * cloud, a Chronicle card and the grove's winter dieback all charge the same
 * ledger without knowing it exists.
 *
 * The charge is **proportional**: the dead head comes off each band at the
 * band's own share of the roll, which is what "a siege kills the town, not
 * the workforce" means in arithmetic. (Starvation in this game costs morale,
 * not lives — `stepUpkeep` has no kill — so there is no starvation site to
 * hook; if one is ever added, it must come through `killUnit` and it will be
 * covered the day it is written.) Removals that are not deaths — the warren
 * releasing a company, the Hollow's toll taking a worker off its own shift —
 * go through `u.kill` and deliberately never reach this: a goblin handed a
 * spear and a hand taken off a working shift are both grown by construction,
 * and the bands are untouched exactly because the loss is an adult's.
 */
export function onVillagerDeath(w: World): void {
  if (agedDeath) return;
  if (agelessPeople(w)) return;
  const d = w.demography;
  const v = Math.max(1, w.counts.villagers);
  d.children = Math.max(0, d.children - d.children / v);
  d.elders = Math.max(0, d.elders - d.elders / v);
}

/** The bands as a panel would print them. Whole heads, summing to the roll. */
export interface DemographySnapshot {
  children: number;
  adults: number;
  elders: number;
  /** The worker pool — adult villagers, the number `assignJobs` employs. */
  hands: number;
  ageless: boolean;
}

export function demographySnapshot(w: World): DemographySnapshot {
  const ageless = agelessPeople(w);
  const children = ageless ? 0 : Math.round(w.demography.children);
  const elders = ageless ? 0 : Math.round(w.demography.elders);
  return {
    children,
    adults: Math.max(0, w.pop - children - elders),
    elders,
    hands: adultVillagers(w),
    ageless,
  };
}
